import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Waypoints } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <Waypoints className="h-5 w-5" />
          <span className="font-heading font-bold">CreateForms.app</span>
        </Link>
        {!token ? (
          <div data-testid="reset-invalid">
            <h1 className="text-3xl font-black">Invalid link</h1>
            <p className="mt-3 text-sm text-neutral-500">This reset link is missing its token.</p>
            <Link to="/forgot-password" className="mt-6 inline-block text-sm font-medium underline" data-testid="reset-request-new">Request a new link</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-600" />
            <h1 className="mt-6 text-3xl font-black">Password updated</h1>
            <p className="mt-3 text-sm text-neutral-500">You can now log in with your new password.</p>
            <Link to="/login" className="mt-6 inline-block" data-testid="reset-login-link">
              <Button className="rounded-full px-8">Log in</Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <div>
              <h1 className="text-3xl font-black">Choose a new password</h1>
              <p className="text-sm text-neutral-500 mt-2">At least 6 characters.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw">New password</Label>
              <Input id="pw" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="h-12" data-testid="reset-password-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input id="confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12" data-testid="reset-confirm-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full h-12" data-testid="reset-submit-button">
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
